import { Link, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { ROUTES } from "@/app/router";
import { useCancelAppointment } from "@/features/consultations/hooks";
import { APPOINTMENT_TYPE } from "@/features/consultations/models";
import type { Appointment } from "@/features/consultations/models";
import { isMedicationRequestAppointment } from "@/features/consultations/utils";
import { LogoLoader } from "@/shared/components";
import { Button, CalendarMinimalisticIcon, Dialog } from "@/shared/ui";

interface RescheduleAppointmentModalProps {
  show: boolean;
  onClose: VoidFunction;
  appointment: Appointment;
}

export const RescheduleAppointmentModal = ({
  show,
  onClose,
  appointment,
}: RescheduleAppointmentModalProps) => {
  const cancelAppointmentMutation = useCancelAppointment();

  const navigate = useNavigate();

  const isMedicationRequest = isMedicationRequestAppointment(appointment.type);

  const handleCancelAppointment = () => {
    cancelAppointmentMutation.mutate(
      {
        id: appointment.id,
        subscriptionId: appointment.subscriptionId ?? undefined,
        appointmentType: appointment.type,
      },
      {
        onSuccess: () => {
          if (isMedicationRequest) {
            return navigate(ROUTES.CONSULTATIONS.SCHEDULE);
          }

          if (appointment.type === APPOINTMENT_TYPE.JOURNEY_SUPPORT) {
            return navigate(ROUTES.COACHING.JOURNEY_SUPPORT_SCHEDULE);
          }

          if (appointment.type === APPOINTMENT_TYPE.DIETITIAN) {
            return navigate(ROUTES.COACHING.DIETITIAN_SCHEDULE);
          }
        },
      },
    );
  };

  const cancelAndRescheduleEnabled =
    appointment.type !== APPOINTMENT_TYPE.MEDICATION_REQUEST_INITIAL &&
    appointment.type !== APPOINTMENT_TYPE.TESTOSTERONE;

  return (
    <Dialog.Root open={show} onOpenChange={onClose}>
      <Dialog.Content>
        <div className="flex h-full flex-col gap-6">
          <Dialog.Header icon={CalendarMinimalisticIcon}>
            <Dialog.Title>{t`Need to reschedule?`}</Dialog.Title>

            <Dialog.Description>
              {t`You can reschedule your appointment up to 7 days after the original date — no need to fill out the intake form again.`}
              <br />
              {cancelAndRescheduleEnabled &&
                t`If you need a later date, please cancel and rebook a new consultation.`}
            </Dialog.Description>
          </Dialog.Header>

          <Dialog.Footer>
            {cancelAndRescheduleEnabled && (
              <Button
                onClick={handleCancelAppointment}
                size="lg"
                variant="secondary"
                color="red"
                disabled={cancelAppointmentMutation.isPending}
              >
                {t`Cancel and rebook`}
              </Button>
            )}

            {appointment.rescheduleUrl && (
              <Button
                size="lg"
                disabled={cancelAppointmentMutation.isPending}
                asChild
              >
                <Link to={appointment.rescheduleUrl}>{t`Reschedule`}</Link>
              </Button>
            )}
          </Dialog.Footer>
        </div>

        <LogoLoader show={cancelAppointmentMutation.isPending} fullscreen />
      </Dialog.Content>
    </Dialog.Root>
  );
};
